import { useEffect, useState } from "react";
import api from "@/lib/api";
import { LOGO, money, FREQ_LABEL } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Loader2, ShieldCheck, AlertTriangle, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Activate() {
  const token = new URLSearchParams(window.location.search).get("token");
  const [state, setState] = useState("loading");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState("monthly");

  useEffect(() => {
    if (!token) { setState("error"); setErr("Missing invitation token."); return; }
    api.get(`/invitations/${token}`)
      .then((r) => { setData(r.data); setState("ready"); })
      .catch((e) => { setState("error"); setErr(e.response?.data?.detail || "This invitation is not valid."); });
  }, [token]);

  const payAndActivate = async () => {
    if (data?.plan_choice) {
      setBusy(true);
      try {
        const r = await api.post(`/invitations/${token}/checkout`, { plan, origin_url: window.location.origin });
        window.location.href = r.data.checkout_url;
      } catch (e) { toast.error(e.response?.data?.detail || "Could not start secure payment"); setBusy(false); }
      return;
    }
    if (!data?.checkout_url) { toast.error("Payment link unavailable"); return; }
    setBusy(true);
    window.location.href = data.checkout_url;
  };

  return (
    <div className="min-h-screen flex flex-col text-[#F4FFF9]"
      style={{ background: "radial-gradient(130% 90% at 50% 0%, #0D3B2D 0%, #071A13 45%, #030B08 100%)" }}>
      <div className="p-6"><img src={LOGO.wordmark} alt="ECOREXS" className="w-[160px] h-auto object-contain" /></div>
      <div className="flex-1 flex items-center justify-center px-6 pb-20">
        <div className="w-full max-w-md bg-white/[0.04] border border-[#67F2B3]/20 backdrop-blur-xl rounded-2xl p-8 animate-fade-up" data-testid="activate-card">
          {state === "loading" && <div className="text-center py-6"><Loader2 className="h-8 w-8 text-[#67F2B3] mx-auto animate-spin" /><p className="text-sm text-[#F4FFF9]/60 mt-4">Loading your invitation…</p></div>}
          {state === "error" && (
            <div className="text-center py-6">
              <AlertTriangle className="h-10 w-10 text-amber-400 mx-auto" />
              <h1 className="font-heading font-bold text-xl mt-4">Invitation unavailable</h1>
              <p className="text-sm text-[#F4FFF9]/60 mt-2" data-testid="activate-error">{err}</p>
            </div>
          )}
          {state === "ready" && data && (
            <>
              <div className="inline-flex items-center justify-center h-12 w-12 rounded-xl bg-[#67F2B3]/12 border border-[#67F2B3]/25 mb-5"><ShieldCheck className="h-6 w-6 text-[#67F2B3]" /></div>
              <h1 className="font-heading font-bold text-2xl">Welcome to ECOREXS, {data.client_name}</h1>
              <p className="text-sm text-[#F4FFF9]/60 mt-2">Review your service below and complete your secure payment. Your client portal unlocks automatically once payment clears.</p>

              <div className="mt-6 rounded-xl border border-[#67F2B3]/15 bg-[#071A13]/50 p-5">
                <div className="font-heading font-semibold text-lg">{data.service_name}</div>
                {data.description && <p className="text-sm text-[#F4FFF9]/55 mt-1">{data.description}</p>}
                {data.plan_choice && data.pricing ? (
                  <PlanPicker pricing={data.pricing} plan={plan} setPlan={setPlan} />
                ) : (
                <div className="mt-4 flex items-end justify-between">
                  <span className="font-mono text-2xl font-bold text-[#67F2B3]">{money(data.amount)}</span>
                  <span className="text-xs text-[#F4FFF9]/60">{data.payment_type === "subscription" ? FREQ_LABEL[data.frequency] : "One-time payment"}</span>
                </div>
                )}
                {data.terms && <p className="text-xs text-[#F4FFF9]/45 mt-3 border-t border-white/5 pt-3">{data.terms}</p>}
              </div>

              <p className="text-xs text-[#F4FFF9]/45 mt-4" data-testid="activate-billing-terms">
                {data.plan_choice && data.pricing
                  ? (plan === "yearly"
                    ? `You pay ${money(data.pricing.annual)} today, billed once per year. Renews automatically every 12 months at ${money(data.pricing.annual)} unless canceled. Cancel anytime from your client portal; cancellation takes effect at the end of the paid year, with access until then and no refund for the remaining period.`
                    : `You pay ${money(data.pricing.monthly)} today, then ${money(data.pricing.monthly)} every month. Renews automatically unless canceled. Cancel anytime from your client portal; access continues through the paid month.`)
                  : data.payment_type === "subscription"
                  ? "By paying, you agree to recurring billing at the frequency shown. Cancel anytime from your client portal."
                  : "This is a one-time payment. No recurring charges."}
              </p>
              <Button onClick={payAndActivate} disabled={busy} data-testid="activate-authorize-btn"
                className="w-full mt-5 bg-[#67F2B3] hover:bg-[#8affca] text-[#071A13] font-semibold py-6 rounded-xl">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{data.plan_choice ? "Continue to Secure Payment" : <>Pay &amp; Activate Your Portal</>} <ArrowRight className="ml-1 h-4 w-4" /></>}
              </Button>
              <p className="text-[10px] text-[#F4FFF9]/35 mt-4 text-center">Secured by Stripe · ECOREXS never stores your card details.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PlanPicker({ pricing, plan, setPlan }) {
  const opts = [
    { id: "monthly", title: "Monthly", price: `${money(pricing.monthly)}/month`, lines: ["Billed monthly"] },
    { id: "yearly", title: "Yearly — Save 20%", price: `${money(pricing.annual)}/year`,
      lines: [`Equivalent to ${money(pricing.equivalent_monthly)}/month`, `Save ${money(pricing.savings)} annually`] },
  ];
  return (
    <div className="mt-4 space-y-2" data-testid="plan-picker" role="radiogroup">
      {opts.map((o) => (
        <button key={o.id} type="button" role="radio" aria-checked={plan === o.id} onClick={() => setPlan(o.id)}
          data-testid={`plan-option-${o.id}`}
          className={`w-full text-left rounded-xl border px-4 py-3 transition-colors ${plan === o.id ? "border-[#67F2B3] bg-[#67F2B3]/10" : "border-white/10 hover:border-[#67F2B3]/40"}`}>
          <div className="flex items-center justify-between gap-3">
            <span className="font-mono text-[11px] uppercase tracking-widest text-[#67F2B3]">{o.title}</span>
            <span className={`h-3.5 w-3.5 rounded-full border ${plan === o.id ? "bg-[#67F2B3] border-[#67F2B3]" : "border-white/30"}`} />
          </div>
          <div className="font-mono text-xl font-bold mt-1">{o.price}</div>
          {o.lines.map((l) => <div key={l} className="text-xs text-[#F4FFF9]/55">{l}</div>)}
        </button>
      ))}
    </div>
  );
}
